import React from 'react';
import { RadioCard } from '../RadioCard';
import { Input } from '../Input';
import { spacing } from '../../theme/spacing';
import { typography } from '../../theme/typography';
import { colors } from '../../theme/colors';

interface Step3Props {
    data: any; // Using loose type matching WizardState['timeAndDelivery']
    onChange: (data: any) => void;
}

export const Step3: React.FC<Step3Props> = ({ data, onChange }) => {

    // Helpers for safe access
    const timeSelection = data.timePreference || '7days';
    const deliverySelection = data.deliveryMethod || 'water';

    const update = (updates: any) => {
        onChange({ ...data, ...updates });
    };

    const updateAddress = (addressUpdates: any) => {
        onChange({
            ...data,
            pickupAddress: {
                ...(data.pickupAddress || {}),
                ...addressUpdates
            }
        });
    };

    const titleStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '28px',
        fontWeight: typography.weights.bold,
        color: colors.text.primary,
        marginBottom: spacing[12],
        marginTop: 0,
    };

    const sectionHeaderStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '16px',
        fontWeight: typography.weights.semiBold,
        color: colors.text.primary,
        marginBottom: spacing[24],
        marginTop: spacing[48],
    };

    const helperStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '16px',
        color: colors.text.muted,
        marginBottom: spacing[48],
        lineHeight: 1.6,
    };

    const subsectionHelperStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '14px',
        color: colors.text.muted,
        marginTop: spacing[8],
        marginBottom: spacing[16],
        lineHeight: 1.5,
    };

    const gridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: spacing[16],
    };

    const fullWidthStyle: React.CSSProperties = {
        gridColumn: '1 / -1',
    };

    return (
        <div>
            <h2 style={titleStyle}>Miten vene päätyy meille, ja milloin se sopisi?</h2>
            <p style={helperStyle}>
                Kerro toiveesi – sovitaan tarkka päivä erikseen kun tiedämme kalenterin tilanteen.
            </p>

            {/* Section 1: Time Preference */}
            <h3 style={sectionHeaderStyle}>Ajankohta</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[16] }}>
                <RadioCard
                    title="Seuraavan 7 päivän aikana"
                    description="Veneesi huolletaan mahdollisimman pian tulevalla viikolla."
                    selected={timeSelection === '7days'}
                    onSelect={() => update({ timePreference: '7days' })}
                />
                <RadioCard
                    title="Seuraavan 30 päivän aikana"
                    description="Joustava vaihtoehto, jos veneesi ei ole päivittäisessä käytössä."
                    selected={timeSelection === '30days'}
                    onSelect={() => update({ timePreference: '30days' })}
                />
                <RadioCard
                    title="Tietty viikko"
                    description="Jos sinulla on toive tietystä viikosta, valitse tämä."
                    selected={timeSelection === 'specific'}
                    onSelect={() => update({ timePreference: 'specific' })}
                />

                {/* Conditional Input for Specific Week */}
                {timeSelection === 'specific' && (
                    <div style={{ paddingLeft: spacing[8] }}>
                        <Input
                            label="Toiveviikko (esim. Viikko 42)"
                            placeholder="Kirjoita toivomasi viikko"
                            helperText="Yritämme parhaamme mukaan järjestää huollon tälle viikolle."
                            value={data.specificWeek || ''}
                            onChange={(e) => update({ specificWeek: e.target.value })}
                        />
                    </div>
                )}
            </div>

            {/* Section 2: Delivery Method */}
            <h3 style={sectionHeaderStyle}>Toimitustapa</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[16] }}>
                <RadioCard
                    title="Tuon veneen vesitse"
                    description="Ajan veneen suoraan huoltolaituriin."
                    selected={deliverySelection === 'water'}
                    onSelect={() => update({ deliveryMethod: 'water' })}
                />
                <RadioCard
                    title="Tuon veneen trailerilla"
                    description="Tuon veneen trailerilla piha-alueelle."
                    selected={deliverySelection === 'trailer'}
                    onSelect={() => update({ deliveryMethod: 'trailer' })}
                />
                <RadioCard
                    title="Hakekaa veneeni"
                    description="Vene noudetaan sovitusta paikasta."
                    selected={deliverySelection === 'pickup'}
                    onSelect={() => update({ deliveryMethod: 'pickup' })}
                />

                {/* Conditional Inputs for Pickup */}
                {deliverySelection === 'pickup' && (
                    <div style={{ marginTop: spacing[16], borderTop: `1px solid #F3F4F6`, paddingTop: spacing[24] }}>
                        <p style={subsectionHelperStyle}>
                            Noudosta veloitetaan lisämaksu matkan mukaan. Kerro mistä vene noudetaan, niin laskemme tarkan hinnan.
                        </p>
                        <div style={gridStyle}>
                            <div style={fullWidthStyle}>
                                <Input
                                    label="Katuosoite"
                                    placeholder="Esim. Venetie 1"
                                    value={data.pickupAddress?.street || ''}
                                    onChange={(e) => updateAddress({ street: e.target.value })}
                                />
                            </div>
                            <Input
                                label="Postinumero"
                                placeholder="00100"
                                value={data.pickupAddress?.zip || ''}
                                onChange={(e) => updateAddress({ zip: e.target.value })}
                            />
                            <Input
                                label="Postitoimipaikka"
                                placeholder="Helsinki"
                                value={data.pickupAddress?.city || ''}
                                onChange={(e) => updateAddress({ city: e.target.value })}
                            />
                            <div style={fullWidthStyle}>
                                <Input
                                    label="Lisätiedot (valinnainen)"
                                    placeholder="Esim. porttikoodi tai tarkemmat ohjeet"
                                    value={data.pickupAddress?.notes || ''}
                                    onChange={(e) => updateAddress({ notes: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
